import type { ReportPreviewPageModel } from 'app/modules/analytics/modules/reports/utils/buildReportPreviewPages';

export interface ReportPreviewSectionNoteVisibility {
  showPerformanceNote: boolean;
  showContentNote: boolean;
}

export interface ReportPreviewSectionNoteOptions {
  performanceNote?: string | null;
  contentNote?: string | null;
}

function pageHasPerformanceContent(page: ReportPreviewPageModel): boolean {
  const hasPerformancePlatformBlocks = (page.platformBlocks ?? []).some(
    (block) => !block.kind.includes('content'),
  );

  return (
    page.chartMetrics.length > 0 ||
    page.performanceTileMetrics.length > 0 ||
    hasPerformancePlatformBlocks
  );
}

function pageHasContentSectionContent(page: ReportPreviewPageModel): boolean {
  const hasContentPlatformBlocks = (page.platformBlocks ?? []).some((block) =>
    block.kind.includes('content'),
  );

  return page.contentMetrics.length > 0 || hasContentPlatformBlocks;
}

function findLastPageIndex(
  pages: ReportPreviewPageModel[],
  predicate: (page: ReportPreviewPageModel) => boolean,
): number {
  for (let index = pages.length - 1; index >= 0; index -= 1) {
    if (predicate(pages[index])) {
      return index;
    }
  }

  return -1;
}

function resolveNotePageIndex(
  lastSectionPageIndex: number,
  hasNote: boolean,
): number {
  if (lastSectionPageIndex >= 0) {
    return lastSectionPageIndex;
  }

  return hasNote ? 0 : -1;
}

export function getReportPreviewSectionNoteVisibility(
  previewPages: ReportPreviewPageModel[],
  pageIndex: number,
  options?: ReportPreviewSectionNoteOptions,
): ReportPreviewSectionNoteVisibility {
  const hasPerformanceNote = Boolean(options?.performanceNote?.trim());
  const hasContentNote = Boolean(options?.contentNote?.trim());

  const performanceNotePageIndex = resolveNotePageIndex(
    findLastPageIndex(previewPages, pageHasPerformanceContent),
    hasPerformanceNote,
  );
  const contentNotePageIndex = resolveNotePageIndex(
    findLastPageIndex(previewPages, pageHasContentSectionContent),
    hasContentNote,
  );

  return {
    showPerformanceNote:
      hasPerformanceNote && pageIndex === performanceNotePageIndex,
    showContentNote: hasContentNote && pageIndex === contentNotePageIndex,
  };
}
